// SEO content registry. Source of truth for keyword landing pages and FAQ.
// Edit this file to add/update SEO landing pages without touching components.

export const SITE_URL = "https://sparkread.net";
export const ORG_NAME = "SparkRead";
export const ORG_LEGAL_NAME = "SparkRead — Adaptive AI Literacy Platform";
export const ORG_LOGO = `${SITE_URL}/sparkread-logo.png`;
export const ORG_SAMEAS = [
  "https://writersspark.net",
];

/**
 * Each landing topic renders at /reading/{slug} via the LandingTopic component.
 * They are designed to rank for high-intent literacy queries.
 */
export const LANDING_TOPICS = [
  {
    slug: "reading-comprehension-strategies",
    title: "Reading Comprehension Strategies that Actually Work",
    h1: "Reading comprehension strategies for every reader.",
    description:
      "Evidence-backed reading comprehension strategies for elementary, middle, and high school students. Build inference, main-idea, summarization, and analytical thinking with SparkRead.",
    keywords: "reading comprehension strategies, comprehension activities, main idea practice, inference practice, summarization practice",
    intro:
      "Strong readers don't just see words — they ask questions, picture the scene, predict, infer, and connect. SparkRead teaches and practices each strategy with adaptive passages, AI-guided annotations, and warm coaching.",
    sections: [
      { h2: "Make and confirm predictions",
        body: "Before reading, scan the title, subheadings, and images. Make a prediction. Read to confirm or revise. SparkRead's annotator turns this into a one-tap habit." },
      { h2: "Find the main idea (and the evidence under it)",
        body: "The main idea is supported by repeated details. Train students to circle the 'thesis sentence' and highlight three supporting pieces of evidence on every passage." },
      { h2: "Infer beyond the words on the page",
        body: "Inference is reading between the lines. Practice with passages tagged 'inference' in the SparkRead library and use the Annotator AI for grade-band-appropriate coaching." },
      { h2: "Summarize without copying",
        body: "Strong summaries restate the central idea in your own words. The AI Companion (Mr. Ramsey) uses scaffolding questions to guide students toward their own summary — building critical thinking and independence." },
    ],
    cta: { to: "/practice", label: "Try a passage" },
  },
  {
    slug: "reading-fluency-support",
    title: "Reading Fluency Support — pacing, expression, and prosody",
    h1: "Reading fluency support that builds stamina.",
    description: "Build reading fluency with read-aloud practice, AI pacing analysis, and warm coaching feedback. Designed for K-12 and ELL readers.",
    keywords: "reading fluency practice, reading fluency app, oral reading practice, pacing analysis, prosody, words per minute",
    intro:
      "Fluency is the bridge between decoding and comprehension. The Fluency Studio records a student's read-aloud, analyzes pacing in WPM, and returns a single growth note — never shame, always specific.",
    sections: [
      { h2: "What fluency actually measures",
        body: "Accuracy, rate (WPM), and prosody — the expression and rhythm of reading. Strong fluency reduces cognitive load so comprehension can take over." },
      { h2: "Record, review, repeat",
        body: "Fluency Studio uses the browser's microphone to record short reads. Students see WPM trends and get a coaching bullet on what to try next." },
      { h2: "ELL-friendly support",
        body: "Adjustable speeds (0.75x – 1.5x) on every TTS read-aloud help ELL readers acquire prosody before independent practice." },
    ],
    cta: { to: "/fluency", label: "Open the Fluency Studio" },
  },
  {
    slug: "literacy-intervention-tools",
    title: "Literacy Intervention Tools for Teachers and Schools",
    h1: "Literacy intervention tools that meet readers where they are.",
    description: "Adaptive literacy intervention tools for Tier 2 and Tier 3 readers. SparkRead's adaptive engine personalizes practice, surfaces struggle signals, and supports intervention groupings.",
    keywords: "literacy intervention platform, reading intervention tools, tier 2 reading, tier 3 reading, struggling readers tool",
    intro:
      "Effective intervention is timely, targeted, and motivating. SparkRead's Adaptive Coaching engine quietly tracks effort, accuracy, and consistency — surfacing the right scaffold for each reader.",
    sections: [
      { h2: "Auto-grouping by grade band and need",
        body: "The Teacher Dashboard auto-groups students by grade band and accuracy signals so you can target intervention quickly." },
      { h2: "Skill-level scaffolds",
        body: "The Annotator AI delivers grade-band-aware analysis on demand. Struggling readers get more scaffolding; advanced readers get a deeper push." },
      { h2: "Motivation that lasts",
        body: "SPARX points, streaks, and badges turn intervention into engagement — not a punishment." },
    ],
    cta: { to: "/teacher", label: "See the teacher view" },
  },
  {
    slug: "help-for-struggling-readers",
    title: "Help for Struggling Readers — gentle, growth-focused practice",
    h1: "Help for struggling readers, built on growth — never shame.",
    description: "Support struggling readers with adaptive passages, AI scaffolding, ELL translations, and the encouraging, rigorous coaching voice of Mr. Ramsey.",
    keywords: "struggling readers tool, reading intervention, support for struggling readers, growth-focused reading",
    intro:
      "Struggling readers don't need pressure — they need a path. SparkRead pairs short, high-interest passages with one-tap AI scaffolding and a coaching voice that celebrates small wins.",
    sections: [
      { h2: "Short passages, big wins",
        body: "Daily passages keep stamina low pressure. Each completion earns SPARX points and reinforces routine." },
      { h2: "One-tap scaffolding",
        body: "Highlight any sentence to get a plain-language explanation, a context clue hint, or an inference question — no shame, just growth." },
      { h2: "ELL-aware support",
        body: "Translate any vocab word into nine languages and use audio read-aloud at gentle speeds." },
    ],
    cta: { to: "/register", label: "Start free" },
  },
  {
    slug: "adaptive-reading-platform",
    title: "Adaptive Reading Platform for K-12 and AP Learners",
    h1: "An adaptive reading platform that grows with every reader.",
    description: "SparkRead's adaptive reading platform personalizes passages, vocabulary, and coaching using effort, accuracy, and consistency signals.",
    keywords: "adaptive reading support, adaptive reading platform, personalized reading practice, AI reading coach",
    intro:
      "SparkRead's adaptive engine watches three signals — effort, accuracy, and consistency — and tunes the experience in real time. The result: practice that feels personal, not generic.",
    sections: [
      { h2: "Signals over scores",
        body: "Effort, accuracy, and consistency reveal more than a single grade. SparkRead surfaces these on the student dashboard so growth becomes visible." },
      { h2: "Coaching that adapts",
        body: "High effort with low accuracy? You'll get a targeted skill drill. High accuracy with low consistency? You'll be invited into a 10-minute routine." },
      { h2: "Passages tuned to interest",
        body: "Students pick hobbies during signup. The library biases passages toward what they love — mystery, sports, mythology, sci-fi, and more." },
    ],
    cta: { to: "/practice", label: "Browse the library" },
  },
  {
    slug: "reading-engagement-tools",
    title: "Reading Engagement Tools that Make Practice Stick",
    h1: "Reading engagement tools students actually want to use.",
    description: "Gamified reading practice with streaks, SPARX points, badges, Vocabulary Duel, and culturally responsive coaching.",
    keywords: "student reading engagement platform, reading engagement tools, reading gamification, motivation for reading",
    intro:
      "Engagement is the first lever of literacy growth. SparkRead turns daily reading into streaks, points, badges, and friendly competition.",
    sections: [
      { h2: "Daily streaks and SPARX points",
        body: "Every quiz, highlight, and vocabulary round earns SPARX. Streaks reward consistency without punishing missed days harshly." },
      { h2: "Vocabulary Duel",
        body: "A 60-second speed run with a global leaderboard turns Tier 2, SAT, ACT, and AP words into a friendly competition." },
      { h2: "Culturally responsive coaching",
        body: "Mr. Ramsey's coaching voice meets every learner where they are — connecting literacy to literature, storytelling, sports, music, gaming, film, history, science, technology, and current events. Rigorous, honest, and never intimidating." },
    ],
    cta: { to: "/duel", label: "Try Vocabulary Duel" },
  },
  {
    slug: "middle-school-reading-support",
    title: "Middle School Reading Support — comprehension, fluency, vocabulary",
    h1: "Middle school reading support that actually moves the needle.",
    description: "Adaptive middle school reading support across comprehension, fluency, and vocabulary. Designed for grades 6-8 with grade-band-aware coaching.",
    keywords: "middle school literacy support, middle school reading, grade 6 reading, grade 7 reading, grade 8 reading practice",
    intro:
      "Middle school is where reading transitions from learning-to-read to reading-to-learn. SparkRead supports the shift with grade-band-aware passages and coaching tuned for 11-14 year olds.",
    sections: [
      { h2: "Grade 6-8 passages with depth",
        body: "Memoir, mythology, informational text, and literary fiction — selected for both rigor and student interest." },
      { h2: "Comprehension that scales up",
        body: "Inference, theme, tone, and figurative language are introduced gradually with rationale-rich quiz feedback." },
      { h2: "Vocabulary that compounds",
        body: "Tier 2 words show up across passages so middle schoolers see the same word in three different contexts within a month." },
    ],
    cta: { to: "/practice", label: "See middle-school passages" },
  },
  {
    slug: "student-literacy-improvement",
    title: "Student Literacy Improvement — measurable, motivating growth",
    h1: "Student literacy improvement, one short practice at a time.",
    description: "Improve student literacy with adaptive practice, AI-guided annotations, and Adaptive Coaching that celebrates growth.",
    keywords: "student literacy improvement, reading skill development, literacy growth, reading practice for students",
    intro:
      "Literacy improvement happens in small, repeated wins. SparkRead's daily passage habit + adaptive coaching makes the math of compound improvement visible.",
    sections: [
      { h2: "Daily passage habit",
        body: "Ten minutes a day, every day, beats two hours once a week. SPARX points reward the right cadence." },
      { h2: "Coaching that celebrates growth",
        body: "Adaptive Coaching always names what's working before suggesting a next step." },
      { h2: "Standards-aware skills",
        body: "Skills mapped to common literacy standards: main idea, inference, central idea, figurative language, tone, rhetoric, evidence." },
    ],
    cta: { to: "/dashboard", label: "Open the dashboard" },
  },
  {
    slug: "test-taking-reading-skills",
    title: "Test-Taking Reading Skills — eliminate, evidence, evaluate",
    h1: "Test-taking reading skills students actually transfer to test day.",
    description: "Teach test-taking reading skills: eliminate wrong answers, identify distractors, evidence tracking, pacing, and multiple-choice reasoning for SAT, ACT, and AP.",
    keywords: "test-taking skill development, multiple choice reading questions, eliminate wrong answers, evidence tracking, SAT reading strategy",
    intro:
      "Test-taking is a teachable skill. The Test-Taking Skills Lab and timed-mode passages teach the reasoning behind each multiple-choice answer, not just the answer itself.",
    sections: [
      { h2: "Eliminate wrong answers",
        body: "Most missed questions are eliminations, not selections. Train students to remove distractors before deciding." },
      { h2: "Evidence tracking",
        body: "Highlight evidence as you read. The Annotator turns evidence-tracking into a habit." },
      { h2: "Pacing under pressure",
        body: "Timed mode adds a 3-minute target with a speed bonus — pressure becomes a teachable feature." },
    ],
    cta: { to: "/test-lab", label: "Open the Test-Taking Lab" },
  },
  {
    slug: "vocabulary-and-context-clues",
    title: "Vocabulary and Context Clues — read smarter, faster",
    h1: "Vocabulary and context clues, taught in real reading.",
    description: "Build vocabulary in context with definition recall, Vocabulary Duel speed runs, context-clue practice, and AI-supported ELL translations in nine languages.",
    keywords: "vocabulary development, context clues practice, SAT vocabulary, ACT vocabulary, AP vocabulary, Tier 2 vocabulary",
    intro:
      "Words you meet in real passages stick. SparkRead's vocabulary system surfaces Tier 2, SAT, ACT, and AP words inside passages — not on dry flashcards.",
    sections: [
      { h2: "Tier 2 and Tier 3 words in real context",
        body: "Words appear inside passages first, then in a Definition Dash card a few days later. That's how vocabulary actually transfers." },
      { h2: "Context-clue practice",
        body: "The Annotator AI can explain how to use context to guess meaning — exactly the skill SAT/ACT tests reward." },
      { h2: "Nine-language ELL support",
        body: "Translate any word into Spanish, French, Mandarin, Arabic, Vietnamese, Korean, Portuguese, Haitian Creole, or Tagalog." },
    ],
    cta: { to: "/vocabulary", label: "Open the Vocabulary Builder" },
  },
];

export const FAQ_ITEMS = [
  {
    q: "What is SparkRead?",
    a: "SparkRead is an adaptive AI-powered literacy platform that helps elementary, middle, high school, AP, SAT, and ACT students improve reading comprehension, vocabulary, fluency, literary analysis, and multiple-choice test-taking through guided practice and warm coaching.",
  },
  {
    q: "Is SparkRead free?",
    a: "Yes. SparkRead has a free plan with daily passages, limited AI coaching, streaks, SPARX points, and two game modes. Premium ($9.99/mo) unlocks unlimited annotations, adaptive coaching, SAT/ACT/AP prep, the Fluency Studio, all voices, and Vocabulary Duel.",
  },
  {
    q: "How does SparkRead help struggling readers?",
    a: "Adaptive Coaching watches three signals — effort, accuracy, and consistency — and tunes scaffolding accordingly. Struggling readers get one-tap AI explanations, gentle TTS read-alouds, and a coaching voice that always names a strength before suggesting a next step.",
  },
  {
    q: "Does SparkRead support ELL students?",
    a: "Yes. Vocabulary words can be translated into nine languages (Spanish, French, Mandarin, Arabic, Vietnamese, Korean, Portuguese, Haitian Creole, Tagalog) and TTS read-aloud supports six voices and adjustable speeds.",
  },
  {
    q: "Is SparkRead aligned to standards?",
    a: "Skills are mapped to common literacy standards including main idea, inference, central idea, figurative language, tone, rhetoric, and evidence. Teachers can group students by grade band and accuracy signals.",
  },
  {
    q: "What devices does SparkRead support?",
    a: "Any modern desktop, tablet, or mobile browser. SparkRead is also installable as a Progressive Web App for app-like access on phones and tablets.",
  },
  {
    q: "How does SparkRead prepare students for SAT, ACT, and AP?",
    a: "Timed mode adds a 3-minute target with a speed bonus, the Test-Taking Skills Lab teaches answer-elimination reasoning, and SAT/ACT/AP-tier vocabulary appears throughout passages.",
  },
  {
    q: "Does SparkRead work for teachers and parents?",
    a: "Yes. Teachers get a class-wide dashboard with auto-grouping by grade band and intervention signals. Parents see a calm, simple view of their reader's growth — streaks, SPARX, accuracy, and badges.",
  },
];

export const BLOG_CATEGORIES = [
  "Reading Comprehension",
  "Literacy Intervention",
  "Reading Fluency",
  "Vocabulary Skills",
  "Test-Taking Skills",
  "Struggling Readers",
  "Student Engagement",
  "Teacher Resources",
];
